// Soft-completion explain view — the mockup-approved section sequence
// for `strategy === "soft_completion"` (soft-completion-mechanics.html
// / explains-page-contract.json). Replaces the generic
// CompletionStrategyPanel content for this strategy; enrichment-gated
// blocks (E1–E7) render nothing on pre-enrichment payloads.
import { useMemo } from "react";
import type {
  AccountRecord,
  HouseholdModelInfo,
  SoftCompletionData,
} from "../../lib/explainTypes";
import type { SoftSectionContext } from "./SoftCompletionContext";
import { buildColorScale } from "./charts";
import {
  UNKNOWN_AC,
  accountById,
  buildSymbolToAssetClass,
} from "./softCompletionLib";
import AccountsModelsSection from "./AccountsModelsSection";
import FitModelAccountsSection from "./FitModelAccountsSection";
import NoModelFillSection from "./NoModelFillSection";
import OvershootRedistributionPanel from "../OvershootRedistributionPanel";
import ProcessingOrderSection from "./ProcessingOrderSection";
import RestrictionsSection from "./RestrictionsSection";
import VerdictStripSection from "./VerdictStripSection";
import VerificationSection from "./VerificationSection";

interface Props {
  completion: SoftCompletionData;
  accounts: AccountRecord[];
  householdModel: HouseholdModelInfo | null;
  resolveAccountName: (accountId: number) => string;
}

export default function SoftCompletionView({
  completion,
  accounts,
  householdModel,
  resolveAccountName,
}: Props) {
  const ctx: SoftSectionContext = useMemo(() => {
    const symbolToAc = buildSymbolToAssetClass(accounts, completion);
    // Budget classes first (stable, sorted) so every chart shares hues;
    // the cash/unknown bucket keys off the scale's gray fallback.
    const acKeys = [
      ...Object.keys(completion.household_target_by_asset_class ?? {}).sort(),
      ...Object.values(symbolToAc),
    ].filter((ac) => ac !== UNKNOWN_AC && ac !== "Unknown");
    return {
      completion,
      accounts,
      accountsById: accountById(accounts),
      householdModel,
      symbolToAc,
      acColor: buildColorScale(acKeys),
      resolveAccountName,
    };
  }, [completion, accounts, householdModel, resolveAccountName]);

  return (
    <>
      <VerdictStripSection ctx={ctx} />
      <AccountsModelsSection ctx={ctx} />
      <ProcessingOrderSection ctx={ctx} />
      <RestrictionsSection ctx={ctx} />
      <FitModelAccountsSection ctx={ctx} />
      <OvershootRedistributionPanel
        data={completion.overshoot_redistribution ?? null}
        resolveAccountName={resolveAccountName}
        acColor={ctx.acColor}
      />
      <NoModelFillSection ctx={ctx} />
      <VerificationSection ctx={ctx} />
    </>
  );
}
